// ===============================================================================
// LandingPage.js - Public Landing Page for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/LandingPage.js
// Forest & Cream Theme - Matching Syros360 design system
// ===============================================================================

import React, { useState, useEffect } from "react";
import "./LandingPage.css";
import ExpenseHelp from "./ExpenseHelp";
import "./ExpenseHelp.css";
import {
  DashboardSimulation,
  BalancesSimulation,
  GroupsSimulation,
  ExpenseDetailsSimulation,
  SettleUpSimulation
} from "./LandingSimulations";
import "./LandingSimulations.css";

function LandingPage({ onNavigateToLogin, onNavigateToSignup }) {
  // ===============================================================================
  // State Management
  // ===============================================================================
  const [scrolled, setScrolled] = useState(false);
  const [activeTagline, setActiveTagline] = useState(0);
  const [showExpenseHelp, setShowExpenseHelp] = useState(false);

  const taglines = [
    "No stress",
    "No headache", 
    "No mistakes"
  ];

  // ===============================================================================
  // Scroll Handler for Navbar
  // ===============================================================================
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ===============================================================================
  // Rotating Tagline Animation
  // ===============================================================================
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTagline((prev) => (prev + 1) % taglines.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [taglines.length]);

  // ===============================================================================
  // Silver Features (Free Tier)
  // ===============================================================================
  const silverFeatures = [
    "Create groups",
    "Invite members to the group",
    "Track group expenses",
    "Setup default Splits",
    "Modify split per expense",
    "Track debts/balances",
    "Spending summaries",
    "Categorize expenses",
    "Multiple currencies",
    "Export expenses (Excel)"
  ];

  // ===============================================================================
  // Gold Features (Premium Tier)
  // ===============================================================================
  const goldFeatures = [
    "Unlimited Groups and Expenses",
    "Advanced Groups (Business, Construction, Loans)",
    "Attach your receipt",
    "Reporting & Charts",
    "Expense analysis",
    "Search feature",
    "Print PDF",
    "No Ads - enjoy without interruptions",
    "Create your own categories",
    "Integration with Power BI"
  ];

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="landing-page">
      {/* ===================================================================
          Navigation Header
          =================================================================== */}
      <nav className={`landing-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="landing-logo">
          <span className="logo-icon">💰</span>
          <div className="logo-text">
            <span className="logo-main">SplitMon</span>
            <span className="logo-tagline">EXPENSE MONITORING SYSTEM</span>
          </div>
        </div>
        
        <div className="nav-actions">
          <button className="nav-btn login" onClick={onNavigateToLogin}>
            Log in
          </button>
          <button className="nav-btn signup" onClick={onNavigateToSignup}>
            Sign up
          </button>
        </div>
      </nav>

      {/* ===================================================================
          Hero Section
          =================================================================== */}
      <section className="hero-section">
        <div className="hero-content">
          <h3 className="hero-title">
            Make sharing expenses with friends, family, housemates, and anyone easier...
            <br />
            <span className="hero-highlight">
              <span className="rotating-text">{taglines[activeTagline]}</span>
            </span>
          </h3>
          
          <p className="hero-subtitle">
            Monitor and track your shared expenses and balances with housemates, 
            trips, groups, friends, and family. Smart expense monitoring made simple.
          </p>
          
          <div className="hero-cta">
            <button className="cta-btn primary" onClick={onNavigateToSignup}>
              Sign up for free
            </button>
            <button className="cta-btn secondary" onClick={onNavigateToLogin}>
              Log in
            </button>
          </div>
          
          <p className="hero-platforms">
            Free for Web, iOS, and Android.
          </p>
        </div>

        <div className="hero-image">
          <div className="phone-mockup">
            {/* Placeholder for screenshot1.png */}
            <DashboardSimulation />
          </div>
        </div>
      </section>

      {/* ===================================================================
          Features Section - Track Balances
          =================================================================== */}
      <section className="feature-section">
        <div className="feature-container">
          <div className="feature-text">
            <h2>Group balances</h2>
            <p>
             Keep shared spending, balances, and outstanding debts organized effortlessly. 
             Real-time monitoring of all your group finances in one place.
            </p>
          </div>
          <div className="feature-image">
            {/* Placeholder for screenshot2.png */}
            <BalancesSimulation />
          </div>
        </div>
      </section>

      {/* ===================================================================
          Features Section - Organize Expenses
          =================================================================== */}
      <section className="feature-section alt">
        <div className="feature-container reverse">
          <div className="feature-text">
            <h2>Organize groups</h2>
            <p>
              Manage shared spending for every occasion, from housemates, travels, friends, and family.
              Create unlimited groups and keep everything organized.
            </p>
            <button 
             className="learn-more-link"
             onClick={() => setShowExpenseHelp(true)}
           >
            See how it works (with different scenarios and examples) →
           </button>
          </div>
          <div className="feature-image">
            {/* Placeholder for screenshot3.png */}
            <GroupsSimulation />
          </div>
        </div>
      </section>

      {/* ===================================================================
          Features Section - Add Expenses Easily
          =================================================================== */}
      <section className="feature-section">
        <div className="feature-container">
          <div className="feature-text">
            <h2>Manage expenses</h2>
            <p>
              The Expense Module helps you track shared expenses within your group and automatically calculates who owes whom. Whether you're splitting a dinner bill, sharing vacation costs, or managing household expenses, this module handles it all. 
              Support for equal splits, percentages, and custom amounts.
            </p>
             <button 
                className="learn-more-link"
                onClick={() => setShowExpenseHelp(true)}
             >
                See how it works (with different scenarios and examples) →
             </button>
          </div>
          <div className="feature-image">
            {/* Placeholder for screenshot4.png */}
            <ExpenseDetailsSimulation />
          </div>
        </div>
      </section>

      {/* ===================================================================
          Features Section - Settle Up
          =================================================================== */}
      <section className="feature-section alt">
        <div className="feature-container reverse">
          <div className="feature-text">
            <h2>Settle balances effortlessly</h2>
            <p>
             Easily track payments and settlements among group members.
             Our smart algorithm minimizes the number of transactions needed.
            </p>
          </div>
          <div className="feature-image">
            {/* Placeholder for screenshot5.png */}
            <SettleUpSimulation />
          </div>
        </div>
      </section>

      {/* ===================================================================
          Features List Section - Silver & Gold
          =================================================================== */}
      <section className="features-list-section">
        <div className="features-list-container">
          <h3 className="features-title">One complete solution. Choose your plan</h3>
          
          <div className="features-columns">
            {/* Silver Features Column */}
            <div className="features-column silver">
              <div className="column-header">
                <span className="coin-icon silver">🪙</span>
                <h3>Core Features</h3>
                <span className="tier-badge silver">FREE</span>
              </div>
              <ul className="features-list">
                {silverFeatures.map((feature, index) => (
                  <li key={index}>
                    <span className="check-icon silver">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Gold Features Column */}
            <div className="features-column gold">
              <div className="column-header">
                <span className="coin-icon gold">🥇</span>
                <h3>Premium Features</h3>
                <span className="tier-badge gold">PRO</span>
              </div>
              <ul className="features-list">
                {goldFeatures.map((feature, index) => (
                  <li key={index}>
                    <span className="check-icon gold">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="features-cta">
            <button className="cta-btn primary large" onClick={onNavigateToSignup}>
              Get Started Free
            </button>
          </div>
        </div>
      </section>

      {/* ===================================================================
          How It Works Section
          =================================================================== */}
      <section className="how-it-works-section">
        <div className="how-container">
          <h2>How SplitMon Works</h2>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">👥</div>
              <h3>Create a Group</h3>
              <p>Start by creating a group for your trip, home, or any shared expense situation.</p>
            </div>
            
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">💳</div>
              <h3>Add Expenses</h3>
              <p>Add expenses as they happen. SplitMon monitors and calculates who owes what.</p>
            </div>
            
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">✅</div>
              <h3>Settle Up</h3>
              <p>When it's time to settle, see simplified debts and record payments easily.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          Final CTA Section
          =================================================================== */}
      <section className="final-cta-section">
        <div className="final-cta-container">
          <h2>Ready to simplify expense sharing?</h2>
          <p>Join thousands of users who trust SplitMon for their expense monitoring needs.</p>
          <button className="cta-btn primary large" onClick={onNavigateToSignup}>
            Sign up for free
          </button>
        </div>
      </section>

      {/* ===================================================================
          Footer
          =================================================================== */}
      <footer className="landing-footer">
        <div className="footer-bottom">
          <p>Made with ❤️ by CSTN Apps</p>
          <p className="copyright">Copyright © 2023 - 2025 CSTN Apps. All rights reserved.</p>
        </div>
      </footer>
      {showExpenseHelp && (
        <div className="expense-help-modal-overlay">
          <div className="expense-help-modal">
            <ExpenseHelp onClose={() => setShowExpenseHelp(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;

